// {{STATE}} resolution for the platform-wide training module (mig 221).
//
// The module is ONE row with organization_id NULL — every brokerage in every
// state reads the same copy — but Laura's beta items 3 and 5 were that it cited
// "Idaho license law" and "a licensed Idaho attorney". So the copy carries a
// {{STATE}} token and each reader's own state is substituted at render.
//
// THE HARD RULE: a literal `{{` must never reach a user. There is no jurisdiction
// for a brand-new org until an admin sets one in Settings › Contract Dates, so
// the fallback is not optional — it is the common case on day one.
//
// Regulator names are deliberately NOT tokenised anywhere in the copy: "Idaho
// Real Estate Commission" has no mechanical equivalent (California = Department
// of Real Estate, Texas = TREC), so inventing 51 agency names would be
// fabricated legal specificity. The copy says "your state's real estate
// commission" instead, which is true everywhere and needs no substitution.

// Relative + explicit .ts, the src/lib convention (see calendarDeadlines.ts):
// this module is imported directly by tests/unit/training-rewrite.test.mjs under
// `node --test`, which has neither the `@/` alias nor extensionless resolution.
import { titleVocabulary } from './legalVocabulary.ts'

/** What replaces {{STATE}} when the org has not chosen a jurisdiction yet. */
export const STATE_FALLBACK = 'your state'

const TOKEN = /\{\{\s*STATE\s*\}\}/g
const TITLE_PROFESSIONAL_TOKEN = /\{\{\s*TITLE_PROFESSIONAL\s*\}\}/g

/**
 * Substitute {{STATE}} in a single string.
 *
 * Tolerates inner whitespace ({{ STATE }}) so a hand-edited migration cannot
 * leak a token on a technicality, and falls back rather than throwing, because a
 * missing state name must never blank out mandatory compliance copy.
 */
export function resolveStateToken(text: string, stateName: string | null | undefined): string {
  const name = stateName?.trim() ? stateName.trim() : STATE_FALLBACK
  return text.replace(TOKEN, name)
}

/**
 * Substitute {{TITLE_PROFESSIONAL}} (mig 244) — who an agent escalates a
 * FACTUAL TITLE question to.
 *
 * Everywhere except Iowa that is the title company. Iowa bans title insurance
 * (Iowa Code § 515.48(10)) and runs a state Title Guaranty programme on
 * attorney title opinions, so the sentence pointed Iowa agents at an industry
 * their state does not have. Resolution lives in legalVocabulary so this and
 * the AI disclaimers cannot end up naming different routes.
 *
 * Unlike {{STATE}} there is no "unknown" degradation: an org with no
 * jurisdiction resolves to the non-Iowa default, which is correct for 50 of 51.
 */
export function resolveTitleProfessionalToken(
  text: string,
  jurisdictionCode: string | null | undefined,
): string {
  return text.replace(TITLE_PROFESSIONAL_TOKEN, titleVocabulary(jurisdictionCode).escalateTo)
}

export interface TokenizedSection {
  id: string
  heading: string
  body: string
}

/**
 * Resolve every section's heading and body.
 *
 * Applied at the DATA boundary (useTrainingStatus) rather than at the render
 * site, so a future consumer of training content cannot forget to call it and
 * ship raw `{{STATE}}` to a licensed agent's screen.
 */
export function resolveTrainingSections<T extends TokenizedSection>(
  sections: T[],
  stateName: string | null | undefined,
  jurisdictionCode?: string | null,
): T[] {
  const resolve = (s: string) =>
    resolveTitleProfessionalToken(resolveStateToken(s, stateName), jurisdictionCode)
  return sections.map((s) => ({
    ...s,
    heading: resolve(s.heading),
    body: resolve(s.body),
  }))
}

/** True when any unresolved template token remains — used by tests and guards. */
export function hasUnresolvedToken(text: string): boolean {
  return text.includes('{{')
}
